import { useState } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';

import { window } from '../../../../globals';
import {
  Menu,
  MenuDivider,
  MenuGroup,
  MenuHeader,
  MenuItem,
  SearchMenuItem,
} from '../../../generic/Menu';
import { Icon, IconText, RouterLink, icons } from '../../../generic';
import Tooltip from '../../../generic/Tooltip';
import type { Team, TeamSelection } from '../../../../types';
import type { Permission } from '../../../../../typings/permissions';
import { ResourceType, Action } from '../../../../../typings/permissions';
import { hasPermission } from '../../../../utils';
import { TEAM_NAME_LENGTH_LIMIT } from './common';
import TeamsModule from '../../../../redux/teams';
import TeamMenuItem from './TeamMenuItem';
import styles from './TeamSelectionMenu.module.less';
import { useDispatchOnMount } from '../../../../hooks';

const VIEW_ANY_PROJECT: Permission = {
  resourceType: ResourceType.ProjectAny,
  action: Action.View,
};

const VIEW_ANY_TEAM: Permission = {
  resourceType: ResourceType.TeamAny,
  action: Action.View,
};

/**
 * Helper for filtering teams by name (case-insensitive).
 */
const filterTeams = (teams: Team[], search: string): Team[] => {
  return teams.filter((team) => team.name.toLowerCase().includes(search.toLowerCase()));
};

/**
 * Helper for getting the userIds associated with a team.
 */
const getTeamUserIds = (team: Team): number[] => {
  const users = team.teamUsers ?? [];
  return users.map((user) => user.userId);
};

/**
 * Helper for deciding what label should represent the team selection dropdown.
 */
const getButtonLabel = (teams: Team[], selection: TeamSelection): ReactNode => {
  // handle unassigned (projects without teams)
  if (selection === 'null') {
    return 'Unassigned projects';
  }
  // handle no teams (all projects)
  if (selection.length === 0) {
    return 'All projects';
  }

  // otherwise combine team names with a comma (and space)
  const selectedTeams = teams.filter((team) => selection.includes(team.id));
  const selectedTeamNames = selectedTeams.map((team) => team.name).join(', ');

  if (selectedTeamNames.length > TEAM_NAME_LENGTH_LIMIT) {
    // handle overflow of one (truncate with ellipsis) or more (just show "N teams") long team names
    const selectedTeamCount = selectedTeams.length;
    const selectedTeamLabel = selectedTeamCount === 1
      ? `${selectedTeamNames.substring(0, TEAM_NAME_LENGTH_LIMIT)}...`
      : `${selectedTeamCount} teams`;

    return (
      <Tooltip id="team-selection-menu-tooltip" tooltip={selectedTeamNames}>
        <span>{selectedTeamLabel}</span>
      </Tooltip>
    );
  }

  return <span>{selectedTeamNames}</span>;
};

type Props = {
  /**
   * Callback for trigger side-effects when the team selection changes.
   */
  onChange?: () => void;
};

/**
 * A dropdown, multi-selection menu for selecting teams.
 */
const TeamSelectionMenu: VoidFunctionComponent<Props> = ({ onChange }) => {
  const dispatch = useDispatch();
  useDispatchOnMount(TeamsModule.actions.fetchAll);

  const userId = window.user?.id as number;
  const userPermissions = window.user?.permissions ?? [];

  const teams = useSelector(TeamsModule.selectors.getTeamList);
  const teamSelection = useSelector(TeamsModule.selectors.getTeamSelection);

  // Dispatch the selection action to the Teams store and fire the onChange handler, if present.
  const setTeamSelection = (selection: TeamSelection) => {
    dispatch(TeamsModule.actions.setTeamSelection(selection));
    if (onChange) {
      onChange();
    }
  };

  const [search, setSearch] = useState<string>('');
  const isSearching = search.length !== 0;

  const selectTeam = (teamId: number): void => {
    // If the selection is currently 'null' (unassigned), select just this team
    if (teamSelection === 'null') {
      setTeamSelection([teamId]);
      return;
    }

    if (teamSelection.includes(teamId)) {
      // Remove the team from the current selection if present
      setTeamSelection(teamSelection.filter((s) => s !== teamId));
    } else {
      // Otherwise add it to the selection
      setTeamSelection([...teamSelection, teamId]);
    }
  };

  const canViewAnyProject = hasPermission(userPermissions, VIEW_ANY_PROJECT);
  const canViewAnyTeam = hasPermission(userPermissions, VIEW_ANY_TEAM);

  const [allMyTeams, allOtherTeams] = _.partition(teams, (team) => getTeamUserIds(team).includes(userId));
  const myTeams = _.sortBy(filterTeams(allMyTeams, search), (team) => team.name.toLowerCase());
  const otherTeams = _.sortBy(filterTeams(allOtherTeams, search), (team) => team.name.toLowerCase());

  const buttonLabel = getButtonLabel(teams, teamSelection);

  return (
    <Menu
      menuButton={(
        <div className={styles.button}>
          <span className={styles.buttonLabel}>{buttonLabel}</span>
          <span className={styles.buttonIcon}>
            <Icon icon={icons.chevronDown} />
          </span>
        </div>
      )}
      onMenuChange={(e) => e.open && setSearch('')}
      menuClassName={styles.menu}
    >
      <SearchMenuItem placeholder="Search teams..." onChange={(e) => setSearch(e.target.value)} />
      {!isSearching && (canViewAnyProject || canViewAnyTeam) && (
        <MenuDivider />
      )}

      {!isSearching && canViewAnyProject && (
        <>
          <TeamMenuItem
            name="All projects"
            isSelected={teamSelection !== 'null' && teamSelection.length === 0}
            onClick={() => setTeamSelection([])}
          />
          <TeamMenuItem
            name="Unassigned projects"
            isSelected={teamSelection === 'null'}
            onClick={() => setTeamSelection('null')}
          />
        </>
      )}

      <MenuDivider />
      <MenuGroup className={styles.menuGroup}>
        <MenuHeader>MY TEAMS</MenuHeader>
        {myTeams.length > 0 ? (
          myTeams.map((team) => (
            <TeamMenuItem
              name={team.name}
              count={team.teamProjectsCount}
              isSelected={teamSelection !== 'null' && teamSelection.includes(team.id)}
              onClick={() => selectTeam(team.id)}
            />
          ))
        ) : (
          <MenuItem disabled>No teams found</MenuItem>
        )}

        <MenuDivider />
        <MenuHeader>OTHER TEAMS</MenuHeader>
        {otherTeams.length > 0 ? (
          otherTeams.map((team) => (
            <TeamMenuItem
              name={team.name}
              count={team.teamProjectsCount}
              isSelected={teamSelection !== 'null' && teamSelection.includes(team.id)}
              onClick={() => selectTeam(team.id)}
            />
          ))
        ) : (
          <MenuItem disabled>No teams found</MenuItem>
        )}
      </MenuGroup>

      {canViewAnyTeam && (
        <>
          <MenuDivider />
          <MenuItem className={styles.itemName}>
            <RouterLink to="/account/settings/organization/teams">
              <IconText icon={icons.gear} text="Manage Teams" />
            </RouterLink>
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default TeamSelectionMenu;
